import { createSelector } from '@reduxjs/toolkit';
import { groupBy, orderBy, uniqBy } from 'lodash-es';
import {
  selectProjectsArray,
  selectSelectedProjectId,
} from 'store/projects/selectors/projectsSelectors';
import { selectSelectedProjectResources } from 'store/resources/selectors/resources.selectors';
import { selectLoggedInUserId } from 'store/user/selectors.js/user.selectors';

const selectFirestopDocumentationState = (state) => state.firestopDocumentation;

export const selectFirestopListsEntities = createSelector(
  [selectFirestopDocumentationState],
  (state) => state.firestopLists.entities
);

export const selectFirestopItemsEntities = createSelector(
  [selectFirestopDocumentationState],
  (state) => state.firestopItems.entities
);

export const selectFirestopImagesEntities = createSelector(
  [selectFirestopDocumentationState],
  (state) => state.firestopImages.entities
);

export const selectFirestopImagesArray = createSelector(
  [selectFirestopImagesEntities],
  (entities) => Object.values(entities)
);

export const selectFirestopListsArray = createSelector(
  [selectFirestopListsEntities],
  (entities) => Object.values(entities)
);

export const selectFirestopItemsArray = createSelector(
  [selectFirestopItemsEntities],
  (entities) => Object.values(entities)
);

export const selectSelectedProjectFirestopLists = createSelector(
  [selectFirestopListsArray, selectSelectedProjectId],
  (firestopLists, selectedProjectId) =>
    firestopLists.filter(
      (firestopList) =>
        firestopList.projectId === selectedProjectId && !firestopList.deletedAt
    )
);
export const selectNotDeletedDefectItemsArray = createSelector(
  [selectFirestopItemsArray],
  (defectItems) => defectItems.filter((defectItem) => !defectItem.deletedAt)
);

export const selectSelectedProjectDefectItems = createSelector(
  [selectNotDeletedDefectItemsArray, selectSelectedProjectId],
  (defectItems, selectedProjectId) =>
    defectItems.filter(
      (defectItem) => defectItem.projectId === selectedProjectId
    )
);

export const selectUsersByRecordStatusByCompany = createSelector(
  (state) => state.defectList.users,
  (users) => {
    console.log('🚀 ~ users', users);
    return users;
  }
);

export const selectFirestopItemsToCreate = createSelector(
  [selectFirestopDocumentationState],
  (state) => state.firestopItemsToCreate
);

export const selectDefectAssignmentsEntities = createSelector(
  [selectFirestopDocumentationState],
  (state) => state.defectAssignments.entities
);

export const selectDefectAssignments = createSelector(
  [selectDefectAssignmentsEntities],
  (defectAssignmentEntities) => Object.values(defectAssignmentEntities)
);

export const selectDefectAssignmentModifications = createSelector(
  [selectFirestopDocumentationState],
  (state) => state.defectAssignmentModifications
);

export const selectFirestopImagesToAdd = createSelector(
  [selectFirestopDocumentationState],
  (state) => state.firestopImagesToAdd
);

export const selectUsersCombined = createSelector(
  selectUsersByRecordStatusByCompany,
  (users) => {
    let combinedObject = {};
    Object.keys(users).forEach((key) => {
      Object.keys(users[key]).map((company) => {
        combinedObject = { ...combinedObject, [company]: users[key][company] };
      });
    });
    return combinedObject;
  }
);

export const selectIsDefectListsDataLoading = createSelector(
  [selectFirestopDocumentationState],
  (state) => state.loading
);

export const selectIsCreateFirestopListDialogOpen = createSelector(
  [selectFirestopDocumentationState],
  (state) => state.isCreateFirestopListDialogOpen
);

export const selectSelectedFirestopListId = createSelector(
  [selectFirestopDocumentationState],
  (state) => state.selectedFirestopListId
);

export const selectIsEditingListAttributes = createSelector(
  [selectFirestopDocumentationState],
  (state) => state.isEditingListAttributes
);

export const selectFirestopListAttributeModifications = createSelector(
  [selectFirestopDocumentationState],
  (state) => state.firestopListAttributeModifications
);

export const selectIsAddNewFirestopDialogOpen = createSelector(
  [selectFirestopDocumentationState],
  (state) => state.isAddNewFirestopDialogOpen
);

export const selectIsCreatingOrUpdatingFirestopData = createSelector(
  [selectFirestopDocumentationState],
  (state) => state.isCreatingOrUpdatingFirestopData
);

export const selectSelectedFirestopItemIdToDelete = createSelector(
  [selectFirestopDocumentationState],
  (state) => state.selectedFirestopItemIdToDelete
);

export const selectSelectedFirestopItemIdToEdit = createSelector(
  [selectFirestopDocumentationState],
  (state) => state.selectedFirestopItemIdToEdit
);

export const selectDefectEditAssignmentsToAdd = createSelector(
  [selectFirestopDocumentationState],
  (state) => state.defectEditAssignmentsToAdd
);

export const selectDefectEditRemovedAssignations = createSelector(
  [selectFirestopDocumentationState],
  (state) => state.defectEditRemovedAssignations
);

export const selectEditedDefectAttributes = createSelector(
  [selectFirestopDocumentationState],
  (state) => state.editedDefectAttributes
);

export const selectFirestopEditImagesToDelete = createSelector(
  [selectFirestopDocumentationState],
  (state) => state.firestopEditImagesToDelete
);

export const selectSelectedDefectItemIdToFix = createSelector(
  [selectFirestopDocumentationState],
  (state) => state.selectedDefectItemIdToFix
);

export const selectSelectedAdditionFirestopItemId = createSelector(
  [selectFirestopDocumentationState],
  (state) => state.selectedAdditionFirestopItemId
);

export const selectDefectFixAttributes = createSelector(
  [selectFirestopDocumentationState],
  (state) => state.defectFixAttributes
);

export const selectFirestopAdditionAttributes = createSelector(
  [selectFirestopDocumentationState],
  (state) => state.firestopAdditionAttributes
);

export const selectDefectFixesEntities = createSelector(
  [selectFirestopDocumentationState],
  (state) => state.defectFixes.entities
);

export const selectFirestopImageDescriptions = createSelector(
  [selectFirestopDocumentationState],
  (state) => state.imageDescriptions
);

export const selectLatestWorkedAtByResource = createSelector(
  [selectFirestopDocumentationState],
  (state) => state.latestWorkedAtByResource
);

const selectDefectsChangeHistoryItemsByItemId = createSelector(
  [selectFirestopDocumentationState],
  (state) => state.changeHistoryItemsByItemId
);

export const selectIsPrintingDefectListPdf = createSelector(
  [selectFirestopDocumentationState],
  (state) => state.isPrintingDefectListPdf
);

export const selectSelectedDefectItemIdToReassign = createSelector(
  [selectFirestopDocumentationState],
  (state) => state.selectedDefectItemIdToReassign
);

export const selectIsConfirmDeleteFirestopListDialogOpen = createSelector(
  [selectFirestopDocumentationState],
  (state) => state.isConfirmDeleteFirestopListDialogOpen
);

export const selectIsDeletingFirestopList = createSelector(
  [selectFirestopDocumentationState],
  (state) => state.isDeletingFirestopList
);

export const selectSelectedDefectItemToReassign = createSelector(
  [selectFirestopItemsEntities, selectSelectedDefectItemIdToReassign],
  (defectItems, defectId) => defectItems[defectId]
);

export const selectHistoryItemsByDefectItemId = createSelector(
  [selectDefectsChangeHistoryItemsByItemId, (_, args) => args],
  (historyItems, args) => historyItems[args]
);

export const selectDefectIdsAssignedToLoggedInUser = createSelector(
  [selectDefectAssignments, selectLoggedInUserId],
  (defectAssignments, loggedInUserId) =>
    defectAssignments
      .filter((defectAssignment) => defectAssignment.userId === loggedInUserId)
      .map((defectAssignment) => defectAssignment.defectId)
);

export const selectSelectedFirestopList = createSelector(
  [selectFirestopListsEntities, selectSelectedFirestopListId],
  (firestopListEntities, selectedFirestopListId) =>
    firestopListEntities[selectedFirestopListId]
);

const selectSelectedFirestopListFirestopItems = createSelector(
  [selectFirestopItemsArray, selectSelectedFirestopListId],
  (firestopItems, selectedFirestopListId) =>
    firestopItems.filter(
      (firestopItem) => firestopItem.listId === selectedFirestopListId
    )
);

export const selectSelectedFirestopListWithModifications = createSelector(
  [selectSelectedFirestopList, selectFirestopListAttributeModifications],
  (firestopList, firestopListModifications) => ({
    ...firestopList,
    ...firestopListModifications,
  })
);

export const selectSelectedFirestopListData = createSelector(
  [selectSelectedFirestopListFirestopItems],
  (listFirestopItems) => {
    const otherInProgressFirestopItems = [];
    const otherFixedFirestopItems = [];

    listFirestopItems.forEach((firestopItem) => {
      if (firestopItem.status === 'COMPLETED') {
        otherFixedFirestopItems.push(firestopItem);
      } else {
        otherInProgressFirestopItems.push(firestopItem);
      }
    });

    const createdAtSortedOtherAssignedInProgressFirestopItems = orderBy(
      otherInProgressFirestopItems,
      'createdAt',
      'desc'
    );

    const createdAtSortedOtherAssignedFixedFirestopItems = orderBy(
      otherFixedFirestopItems,
      'createdAt',
      'desc'
    );

    const workerSortedFirestopItems = [
      ...createdAtSortedOtherAssignedInProgressFirestopItems,
      ...createdAtSortedOtherAssignedFixedFirestopItems,
    ];

    return {
      firestopItems: workerSortedFirestopItems,
    };
  }
);

export const selectDefectsSummaryData = createSelector(
  [selectSelectedProjectFirestopLists, selectSelectedProjectDefectItems],
  (defectLists, defectItems) => {
    const defectListsAmount = defectLists.length;
    const defectItemsAmount = defectItems.length;

    const fixedDefectItems = [];
    const inProgressDefectItems = [];

    defectItems.forEach((defectItem) => {
      if (defectItem.status === 'COMPLETED') {
        fixedDefectItems.push(defectItem);
      } else {
        inProgressDefectItems.push(defectItem);
      }
    });

    const totalFixedDefects = fixedDefectItems.length;
    const totalInProgressDefects = inProgressDefectItems.length;

    return {
      defectListsAmount,
      defectItemsAmount,
      totalFixedDefects,
      totalInProgressDefects,
    };
  }
);

export const selectDefectsAssigneeEmployerAndEmployeeOptions = createSelector(
  [
    selectSelectedProjectResources,
    selectProjectsArray,
    selectLatestWorkedAtByResource,
    (_, args) => args,
  ],
  (resources, projects, latestWorkedAtByResource, assigneeSearchText) => {
    const resourcesWithLatestWorkedAt = resources.map((resource) => ({
      ...resource,
      latestWorkedAt: latestWorkedAtByResource[resource.id]
        ? latestWorkedAtByResource[resource.id]
        : undefined,
    }));

    const latesWorkedAtSortedResources = orderBy(
      resourcesWithLatestWorkedAt,
      'latestWorkedAt',
      'asc'
    );

    const employerBusinessIds = uniqBy(
      resources.map((resource) => resource.employerBusinessId)
    );

    let employerProjects = [];

    employerBusinessIds.forEach((businessId) => {
      const foundEmployer = projects.find(
        (project) =>
          project.businessTypeEnum === 'EMPLOYER' &&
          project.business_id === businessId
      );

      if (foundEmployer) {
        if (
          (assigneeSearchText &&
            foundEmployer.name.toLowerCase().includes(assigneeSearchText)) ||
          !assigneeSearchText
        ) {
          employerProjects.push(foundEmployer);
        }
      }
    });

    const uniqueUserResourcesOfEmployer = uniqBy(
      latesWorkedAtSortedResources,
      (resource) => [resource.user, resource.employer, resource.project].join()
    );

    let resourcesThatHaveWorked = [];

    let resourcesThatHaveNotWorked = [];

    uniqueUserResourcesOfEmployer.forEach((resource) => {
      if (resource.latestWorkedAt) {
        resourcesThatHaveWorked.push(resource);
      } else {
        resourcesThatHaveNotWorked.push(resource);
      }
    });

    const latestWorkedAtOrderedResources = orderBy(
      resourcesThatHaveWorked,
      'latestWorkedAt',
      'desc'
    );

    const employeeResourcesGroupedByBusinessId = groupBy(
      [...latestWorkedAtOrderedResources, ...resourcesThatHaveNotWorked],
      'employerBusinessId'
    );

    return {
      employerOptions: employerProjects,
      employeeOptions: employeeResourcesGroupedByBusinessId,
    };
  }
);

export const selectDefectItemAssignmentsDataByDefectId = createSelector(
  [selectDefectAssignments, (_, args) => args],
  (defectAssignments, args) => {
    const thisDefectItemAssignments = defectAssignments.filter(
      (assignment) => assignment.defectId === args
    );

    let defectAssignedBusinesses = [];
    let defectUserAssignments = [];

    thisDefectItemAssignments.forEach((defectAssignment) => {
      if (
        defectAssignment.businessId &&
        !defectAssignedBusinesses.find(
          (assignmentBusiness) =>
            assignmentBusiness.businessId === defectAssignment.businessId
        )
      ) {
        defectAssignedBusinesses.push(defectAssignment);
      }

      if (defectAssignment.userId) {
        defectUserAssignments.push(defectAssignment);
      }
    });

    return {
      assignmentBusinesses: defectAssignedBusinesses,
      defectUserAssignments,
    };
  }
);

export const selectSelectedFirestopItemToEdit = createSelector(
  [selectFirestopItemsEntities, selectSelectedFirestopItemIdToEdit],
  (firestopItems, selectedFirstopItemId) => firestopItems[selectedFirstopItemId]
);

export const selectEditDefectExistingAssignments = createSelector(
  [
    selectDefectAssignments,
    selectSelectedFirestopItemIdToEdit,
    selectDefectEditAssignmentsToAdd,
    selectDefectEditRemovedAssignations,
  ],
  (
    defectAssignments,
    selectedDefectId,
    addedAssignments,
    removedAssignments
  ) => {
    const selectedDefectAssignments = defectAssignments.filter(
      (assignment) => assignment.defectId === selectedDefectId
    );

    const selectedDefectAddedAssignmentsArray = Object.values(addedAssignments);

    let removedBusinessAssignments = [];
    let removedUserAssignments = [];

    Object.values(removedAssignments).forEach((removedAssignment) => {
      if (!removedAssignment.userId) {
        removedBusinessAssignments.push(removedAssignment.businessId);
      } else {
        removedUserAssignments.push(removedAssignment.userId);
      }
    });
    const filteredExistingAssignments = selectedDefectAssignments.filter(
      (defectAssignment) => {
        const isFoundInRemovedBusinesses = removedBusinessAssignments.includes(
          defectAssignment.businessId
        );

        const isFoundInRemovedUserAssignments = removedUserAssignments.includes(
          defectAssignment.userId
        );
        if (isFoundInRemovedBusinesses || isFoundInRemovedUserAssignments) {
          return false;
        } else {
          return true;
        }
      }
    );

    return [
      ...filteredExistingAssignments,
      ...selectedDefectAddedAssignmentsArray,
    ];
  }
);

export const selectSelectedProjectFirestopImages = createSelector(
  [selectFirestopImagesArray, selectSelectedProjectId],
  (firestopImages, selectedProjectId) =>
    firestopImages.filter(
      (firestopImage) => firestopImage.projectId === selectedProjectId
    )
);

export const selectFirestopImagesByFirestopId = createSelector(
  [selectSelectedProjectFirestopImages, (_, args) => args],
  (firestopImages, args) => {
    const thisFirestopImages = firestopImages.filter(
      (firestopImage) => firestopImage.firestopItemId === args
    );

    const createdAtSortedImages = orderBy(
      thisFirestopImages,
      'createdAt',
      'asc'
    );

    return createdAtSortedImages;
  }
);

export const selectSelectedFirestopItemImages = createSelector(
  [
    selectFirestopImagesArray,
    selectSelectedFirestopItemIdToEdit,
    selectFirestopEditImagesToDelete,
  ],
  (firestopImages, selectedFirestopId, imagesAddedForDeletion) =>
    firestopImages.filter(
      (defectImage) =>
        defectImage.defectId === selectedFirestopId &&
        !imagesAddedForDeletion.includes(defectImage.id)
    )
);

export const selectDefectItemsCountTotalsByDefectListId = createSelector(
  [selectSelectedProjectDefectItems, (_, args) => args],
  (defectItems, defectListId) => {
    const defectListDefectItems = defectItems.filter(
      (defectItem) => defectItem.defectListId === defectListId
    );

    const fixedDefectItems = [];
    const inProgressDefectItems = [];

    defectListDefectItems.forEach((defectItem) => {
      if (defectItem.status === 'COMPLETED') {
        fixedDefectItems.push(defectItem);
      } else {
        inProgressDefectItems.push(defectItem);
      }
    });

    const defectItemsAmount = defectListDefectItems.length;
    const totalFixedDefects = fixedDefectItems.length;
    const totalInProgressDefects = inProgressDefectItems.length;

    return {
      defectItemsAmount,
      totalFixedDefects,
      totalInProgressDefects,
    };
  }
);
